import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Field, Form, Formik, useFormikContext } from "formik";
import Input from "components/Input";
import Dropdown from "components/Dropdown";
import GradeRangeDropdown from "components/GradeRangeDropdown";
import ImageUpload from "components/ImageUpload";
import { GoogleMaps, GoogleMapsRef } from "components/GoogleMaps";
import ResetAndSubmit from "components/ResetAndSubmit";
import * as Yup from "yup";
import { AppContext } from "state";
import { getImageValidationSchema } from "constants/validationSchemas";
import FormWrapper from "components/FormWrapper";
import FotorImageUpload from "components/ImageUpload/FotorImageUpload";
import NewFotorImageUpload from "components/ImageUpload/NewFotorImageUpload";
import axios from "axios";
import { API_CONSTANT_ONBOARDING } from "utils";

const initialValues = {
  NAME: "",
  LOGO: "",
  EMAIL: "",
  SECONDARY_EMAIL: "",
  COUNTRY_CODE: "",
  GOOGLE_MAP_POSITION_NAME: "",
  DOMAIN_NAME: "",
  GOOGLE_MAP_POSITION: "",
  INSTITUTE_TYPE: "",
  BOARD_TYPE: "",
  FOUNDING_DATE: "",
  INSTRUCTION_MEDIUM: [],
  GRADE_LEVELS: [],
  TOTAL_STUDENTS_COUNT: "",
  Number_Of_Branches: "",
  Mobile_Number: "",
  company_name: "",
};

const validationSchema = Yup.object({
  NAME: Yup.string().required("Institute name is Required"),
  EMAIL: Yup.string().required("Institute email is Required"),
  TOTAL_STUDENTS_COUNT: Yup.string()
    .required("Total Students Count is Required")
    .matches(/^\d+$/, "Student Count must contain only digits"),
 Number_Of_Branches: Yup.string()
    .required("Number of branches is Required")
    .matches(/^\d+$/, "Number of branches must contain only digits"),
  LOGO: getImageValidationSchema("Institute logo is Required"),
  GOOGLE_MAP_POSITION: Yup.object().required("Required"),
  INSTITUTE_TYPE: Yup.string().required(" Institute type is Required"),
  DOMAIN_NAME: Yup.string().required(" Domain is Required"),
  BOARD_TYPE: Yup.string().required("Board type is Required"),
  // COUNTRY_CODE: Yup.string().required("Code is required"),
  Mobile_Number: Yup.string()
    .required("Mobile number is required")
    .matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits"),

  FOUNDING_DATE: Yup.string()
    .required("Institute founding date is Required")
    .matches(
      /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[012])\/\d{4}$/,
      "Please enter a valid date (DD/MM/YYYY)"
    )
    .test(
      "valid-year",
      "Year must be between 1800 and current year",
      function (value) {
        if (!value) return false;
        const parts = value.split("/");
        if (parts.length !== 3) return false;
        const year = parts[2];
        const yearNum = parseInt(year, 10);
        const currentYear = new Date().getFullYear();
        return yearNum >= 1800 && yearNum <= currentYear;
      }
    ),
  INSTRUCTION_MEDIUM: Yup.array().min(1, "Institute medium is Required"),
  GRADE_LEVELS: Yup.array().min(1, "Atleast a grade is Required"),
});

const AutoSave = () => {
  const { values } = useFormikContext();
  useEffect(() => {
    sessionStorage.setItem("Institute", JSON.stringify(values));
    window.dispatchEvent(new Event("formUpdated"));
  }, [values]);
  return null;
};

const LF = ({
  handleNavigate,
  resetFormData,
  titleAndDesNotRequired,
  disableForm,
}: {
  handleNavigate;
  resetFormData?: any;
  titleAndDesNotRequired?: boolean;
  disableForm?: boolean;
}) => {
  const { globalData } = useContext(AppContext);

  const loadInitialData = () => {
    try {
      return JSON.parse(sessionStorage.getItem("Institute")) || initialValues;
    } catch (err) {
      return initialValues;
    }
  };
  const [initiateError, setInitiateError] = useState("");
  const mapRef = useRef<GoogleMapsRef>(null);

  const formInitialValues = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("Institute")) || initialValues;
    } catch (err) {
      return initialValues;
    }
  }, []);

  return (
    <Formik
      initialValues={formInitialValues}
      enableReinitialize
      validateOnMount={true}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        sessionStorage.setItem("Institute", JSON.stringify(values));
        setInitiateError("");
        try {
          const payload: { email?: string; phone?: string; company_name: string } = {
            company_name: values.company_name || "",
          };
          if (values.EMAIL) payload.email = values.EMAIL;
          if (values.Mobile_Number) payload.phone = values.Mobile_Number;
          const res = await axios.post(`${API_CONSTANT_ONBOARDING}/master/onboarding/initiate`, payload);
          if (res?.data?.data?.session_token) {
            sessionStorage.setItem("_INITIATE_TOKEN_", res.data.data.session_token);
          }
          handleNavigate();
        } catch (err: any) {
          const status = err?.response?.status;
          const detail = err?.response?.data?.detail;
          if (status === 429) {
            setInitiateError("Too many requests. Please wait a while before trying again.");
          } else {
            setInitiateError(detail || "Something went wrong. Please try again.");
          }
        }
      }}
    >
      {({ handleSubmit, resetForm, errors, values, isValid }) => (
        <FormWrapper
          childern={
            <Form onSubmit={handleSubmit} noValidate className="flex flex-col  w-full ">
              <div className="p-[1.5vw] border-[0.5px] border-gray-300 rounded-[0.73vw] bg-[#FFFFFF] flex flex-col pb-[3vh] ">
                <AutoSave />
                <Field name="company_name">
                  {({ field }) => (
                    <input
                      {...field}
                      type="text"
                      autoComplete="off"
                      tabIndex={-1}
                      aria-hidden="true"
                      style={{ position: "absolute", opacity: 0, pointerEvents: "none", height: 0, width: 0, border: "none" }}
                    />
                  )}
                </Field>
                <div className="flex-1 flex flex-col justify-between ">
                  <section className="flex flex-col">
                    {/* Reduced text size and margin by 10% */}
                    <h2 className="font-inter font-[600] text-[2.3vh] text-[#111827] mb-[1.35vh] tracking-[-0.01em]">
                      General Information
                    </h2>

                    {/* Reduced gaps by 10% */}
                    <div className="grid grid-cols-4 gap-x-[1.3vw] gap-y-[2.5vh] sm:grid-cols-1 md:grid-cols-1">
                      <Input
                        disable={disableForm}
                        name="NAME"
                        backgroundColor="white"
                        label="Institution Name"
                        placeHolder="Enter Institution name"
                        required
                      />
                      <Input
                        disable={disableForm}
                        name="FOUNDING_DATE"
                        type="date"
                        backgroundColor="white"
                        label="Founding date"
                        placeHolder="DD/MM/YYYY"
                        required
                      />
                      <Input
                        disable={disableForm}
                        name="DOMAIN_NAME"
                        backgroundColor="white"
                        label="Sub domain"
                        placeHolder="Enter Subdomain"
                        dominCheck
                        wantButton={".soomuch.ai"}
                        required
                      />
                      <Input
                        label="Mobile Number"
                        name="Mobile_Number"
                        type="number"
                        isMobile={true}
                        countryCodeName="COUNTRY_CODE"
                        placeHolder="Enter 10-digit mobile number"
                        required
                      />

                    </div>
                  </section>
                  {/* Reduced margin by 10% */}
                  <hr className="my-[1.5vh]" style={{ border: 'none', borderTop: '1px solid #D6D4CC' }} />
                  <section className="flex-[2] flex flex-col justify-between">
                    {/* Reduced text size and margin by 10% */}
                    <h2 className="font-inter font-[600] text-[2.3vh] text-[#111827] mb-[1.35vh] tracking-[-0.01em]">
                      Academic details
                    </h2>

                    {/* Reduced gaps by 10% */}
                    <div className="grid grid-cols-3 gap-x-[1.3vw] gap-y-[2.5vh] sm:grid-cols-1 md:grid-cols-1 flex-1">
                      <Dropdown
                        disable={disableForm}
                        name="INSTITUTE_TYPE"
                        label="Institution Type"
                        placeHolder="Pick Institution Type"
                        options={globalData?.InstitutionType}

                      />
                      <Dropdown
                        disable={disableForm}
                        name="INSTRUCTION_MEDIUM"
                        label="Medium of Instruction"
                        placeHolder="Select medium"
                        required
                        isMulti
                        options={globalData?.Instruction_Medium}
                      />
                      <Dropdown
                        isCreatable
                        disable={disableForm}
                        name="BOARD_TYPE"
                        label="Board Type"
                        placeHolder="Pick board type"
                        options={globalData?.BoardType}
                        required
                      />
                      <GradeRangeDropdown
                        isCreatable
                        disable={disableForm}
                        name="GRADE_LEVELS"
                        label="Grade/Class Levels"
                        placeHolder="Pick grades"
                        required
                        options={globalData?.GradeLevels}
                      />
                      <Input
                        disable={disableForm}
                        label="Number Of Branches"
                        name={`Number_Of_Branches`}
                        type="number"
                        placeHolder="Enter number of branches"
                        required
                      />
                      <Input
                        disable={disableForm}
                        label="Total Student Count"
                        name={`TOTAL_STUDENTS_COUNT`}
                        type="number"
                        placeHolder="Enter students count"
                        required
                      />

                      {/* Reduced gap by 10% */}
                      <div className="flex col-span-3 w-full gap-[1.3vw]">

                        <Input
                          disable={disableForm}
                          type="email"
                          name="EMAIL"
                          backgroundColor="white"
                          label="Email"
                          placeHolder="Enter email id"
                          helperText="Helps us send further details"
                        />

                        <Dropdown
                          isCreatable
                          disable={disableForm}
                          name="ADDON_PROGRAMS"
                          label="AddOn Programs"
                          placeHolder="Select Programs"
                          isMulti
                          options={globalData?.AddonPrograms}
                        />
                        <Input
                          disable={disableForm}
                          mapField
                          name="GOOGLE_MAP_POSITION_NAME"
                          mapName="GOOGLE_MAP_POSITION"
                          backgroundColor="white"
                          label="Location"
                          placeHolder="Enter location (manually or via map)"
                          onClick={(e) => {
                            e.preventDefault();
                            mapRef.current?.openDrawer();
                          }}
                          mapButton={
                            <Field name="GOOGLE_MAP_POSITION">
                              {({ field, form, meta }) => (
                                <GoogleMaps
                                  disable={disableForm}
                                  ref={mapRef}
                                  onLocationClick={(e) => {
                                    form.setFieldValue(
                                      "GOOGLE_MAP_POSITION",
                                      e
                                    );
                                    form.setFieldValue(
                                      "GOOGLE_MAP_POSITION_NAME",
                                      e?.address || ""
                                    );
                                    if (e?.city) {
                                      form.setFieldValue("city", e.city);
                                    }
                                    if (e?.state) {
                                      form.setFieldValue("state", e.state);
                                    }
                                    if (e?.country) {
                                      form.setFieldValue("country", e.country);
                                    }
                                    if (e?.postalCode) {
                                      form.setFieldValue(
                                        "zip_code",
                                        e.postalCode
                                      );
                                    }
                                  }}
                                  selectedLocation={field.value}
                                  initialLocation={field.value}
                                  isError={meta.touched && meta.error}
                                />
                              )}
                            </Field>
                          }
                        />
                      </div>

                      {!disableForm && (
                        /* Reduced gap by 10% */
                        <div className="flex col-span-3 gap-[0.29vh]">
                          <Field name="LOGO">
                            {({ field, form, meta }) => (
                              <NewFotorImageUpload
                                onChange={(item) => {
                                  form.setFieldValue("LOGO", item);
                                }}
                                imageValue={field.value}
                                label="Upload Logo"
                                customMessage=""
                                accept={{ "image/jpeg": [".jpeg", ".jpg"] }}
                                disable={disableForm}
                                isError={!!meta.touched && !!meta.error}
                              />
                            )}
                          </Field>
                        </div>
                      )}
                    </div>
                  </section>
                </div>
              </div>

              {initiateError && (
                <p className="text-sm text-red-500 text-right mt-2">{initiateError}</p>
              )}
              {/* Reset Component wrapper untouched per instructions */}
              <div className="flex items-end justify-end mt-0  mb-0 shrink-0">
                <ResetAndSubmit
                  submitToolTip={
                    !isValid &&
                    "Please fill all required fields for the next step."
                  }
                  isError={false}
                />

              </div>
            </Form>
          }
        />
      )}
    </Formik>
  );
};

export default LF;